import { useRef, useState } from "react";
import { useHotkey } from "@tanstack/react-hotkeys";
import { useQuery } from "@tanstack/react-query";
import { useRouter, useRouterState } from "@tanstack/react-router";
import {
  GameQuickNavDialog,
  type GameQuickNavGroup,
} from "@softmax/product-ux/theater-chrome";
import { watchPath } from "@softmax/web-features/leagues";
import { useActualViewer } from "@softmax/web-features/session/records";
import { siteFlagsQueryOptions } from "@/features/site-shell/records";
import { observatoryFlags } from "@softmax/web-features/flags";
import { useDiscardUnavailableData } from "@softmax/web-features/data";
import {
  leagueDirectoryQueryOptions,
  type LeagueDirectoryEntry,
} from "@softmax/web-features/site/queries";

export function GameQuickNav({ enabled }: { readonly enabled?: boolean }) {
  const href = useRouterState({ select: (state) => state.location.href });
  const flags = useQuery({
    ...siteFlagsQueryOptions(),
    enabled: enabled === undefined,
  });
  const visible =
    enabled ??
    (flags.data !== undefined &&
      flags.data.flags[observatoryFlags.leagueQuickNav] !== false);
  return visible ? <EnabledGameQuickNav key={href} /> : null;
}

function EnabledGameQuickNav() {
  const viewer = useActualViewer();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const options = leagueDirectoryQueryOptions({
    viewerId: viewer.data?.subject_id ?? null,
  });
  const catalog = useQuery({
    ...options,
    enabled: open && viewer.isSuccess,
  });
  const accessDenied = useDiscardUnavailableData({
    queryKey: options.queryKey,
    error: catalog.error,
  });
  useHotkey(
    ["Tab", "Q"],
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      const activeElement = document.activeElement;
      returnFocusRef.current =
        activeElement instanceof HTMLElement ? activeElement : null;
      setQuery("");
      setOpen(true);
    },
    {
      enabled: !open,
      ignoreInputs: false,
      preventDefault: false,
      stopPropagation: false,
      timeout: 1000,
    },
  );

  const leagues = accessDenied ? [] : (catalog.data?.entries ?? []);
  const terms = query.toLocaleLowerCase().trim().split(/\s+/);
  const byCoworld = new Map<
    string,
    { readonly label: string; readonly entries: LeagueDirectoryEntry[] }
  >();

  for (const league of leagues) {
    const key = league.coworld ?? league.route.game.id;
    const group = byCoworld.get(key) ?? {
      label: league.coworld ?? league.name,
      entries: [],
    };
    group.entries.push(league);
    byCoworld.set(key, group);
  }

  const groups: GameQuickNavGroup[] = [...byCoworld.entries()]
    .toSorted(([a], [b]) => a.localeCompare(b))
    .map(([value, { label, entries }]) => {
      const group: GameQuickNavGroup = {
        value,
        items: entries
          .filter((league) => {
            const searchable =
              `${league.name} ${league.coworld ?? ""}`.toLocaleLowerCase();
            return terms.every((term) => searchable.includes(term));
          })
          .toSorted((a, b) => a.name.localeCompare(b.name))
          .map((league) => ({
            value: watchPath({ route: league.route, params: {} }),
            label: league.unpublished
              ? `${league.name} · private`
              : league.name,
          })),
      };

      if (entries.length > 1) return { ...group, label };

      return group;
    })
    .filter((group) => group.items.length > 0);

  return (
    <GameQuickNavDialog
      open={open}
      query={query}
      groups={groups}
      loading={catalog.isFetching || viewer.isPending}
      error={
        viewer.isError
          ? "Could not check your access."
          : catalog.isError
            ? catalog.data !== undefined && !accessDenied
              ? "Could not refresh games. Showing the last successful list."
              : "Could not load games."
            : null
      }
      onOpenChange={setOpen}
      onQueryChange={setQuery}
      returnFocusRef={returnFocusRef}
      onRetry={() => {
        if (viewer.isError) void viewer.refetch();
        else void catalog.refetch();
      }}
      onSelect={(href) => {
        setOpen(false);
        void router.navigate({ href });
      }}
    />
  );
}
